import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import _noop from 'lodash/noop';

import ChevronDownIcon from '@ott/icon-chevron-down';
import CrossIcon from '@ott/icon-cross';

import cls from 'classnames';

import _isArray from 'lodash/isArray';

import { TRIGGER_SIZES, DEFAULT } from '../consts';
import styles from './DropdownTrigger.scss';

const DropdownNextTrigger = ({
  setOpenState,
  onClick,
  onResetButtonClick,
  closeOverlay,
  size,
  isOpen,
  isFocused,
  isError,
  value,
  label,
  placeholder,
  className,
  disabled,
  name,
  TriggerRenderer,
  triggerRendererProps,
  hasValue,
  isMultiple,
  shouldHighlightIfHasValue,
  shouldShowResetButton,
  shouldKeepTriggerOpened,
  hasBorder,
  ...restTriggerProps
}) => {
  const buttonSize = TRIGGER_SIZES[size];

  const handleResetButtonClick = () => {
    closeOverlay();
    onResetButtonClick();
  };

  return (
    <div className={styles.triggerWrapper}>
      <button
        name={name}
        type="button"
        className={cls(styles.button, styles[`button--${buttonSize}`], className, {
          [styles.noBorder]: !hasBorder,
          [styles.isError]: isError,
          [styles.isFocused]: (isFocused || shouldKeepTriggerOpened) && !isError && !disabled,
          [styles.isDisabled]: disabled,
          [styles.highlighted]: shouldHighlightIfHasValue && hasValue,
          [styles.withResetButton]: shouldShowResetButton && hasValue,
        })}
        onClick={onClick}
        disabled={disabled}
        data-error-type="new"
        {...restTriggerProps}
      >
        <TriggerRenderer
          value={value}
          label={label}
          placeholder={placeholder}
          isOpen={isOpen}
          isMultiple={isMultiple}
          shouldKeepTriggerOpened={shouldKeepTriggerOpened}
          {...triggerRendererProps}
        />
      </button>
      {shouldShowResetButton && hasValue && (
        <div
          className={cls(styles.resetFilterButton, {
            [styles.highlighted]: shouldHighlightIfHasValue && hasValue,
            [styles.small]: size === 'small',
            [styles.isFocused]: isFocused || shouldKeepTriggerOpened,
          })}
          onClick={handleResetButtonClick}
        >
          <CrossIcon />
        </div>
      )}
    </div>
  );
};

export const DefaultTriggerRenderer = ({
  value,
  label,
  placeholder,
  isOpen,
  resolveContentField,
  isMultiple,
  shouldKeepTriggerOpened,
}) => {
  return (
    <div
      className={cls(styles.triggerInner, {
        [styles.withTitle]: Boolean(label),
      })}
    >
      <div className={styles.textContainer}>
        <div className={styles.title}>{label}</div>
        <div className={styles.text}>
          {resolveContentField({
            value,
            isMultiple,
            placeholder,
          }) || placeholder}
        </div>
      </div>
      <ChevronDownIcon
        className={cls(styles.arrow, { [styles.isOpen]: isOpen || shouldKeepTriggerOpened })}
      />
    </div>
  );
};

export const defaultResolveContentField = ({ value, isMultiple = false, placeholder }) => {
  if ((!_isArray(value) && value) || (_isArray(value) && value.length > 0)) {
    if (isMultiple) {
      return placeholder;
    }

    return value.name || value.label;
  }

  return null;
};

DefaultTriggerRenderer.propTypes = {
  value: PropTypes.any,
  label: PropTypes.string,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isOpen: PropTypes.bool,
  resolveContentField: PropTypes.func,
  isMultiple: PropTypes.bool,
  shouldKeepTriggerOpened: PropTypes.bool,
};

DefaultTriggerRenderer.defaultProps = {
  resolveContentField: defaultResolveContentField,
};

DropdownNextTrigger.propTypes = {
  onClick: PropTypes.func.isRequired,
  size: PropTypes.oneOf(Object.keys(TRIGGER_SIZES)),
  isOpen: PropTypes.bool.isRequired,
  isError: PropTypes.bool,
  isFocused: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.node, PropTypes.object]),
  label: PropTypes.string,
  className: PropTypes.string,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  disabled: PropTypes.bool,
  TriggerRenderer: PropTypes.func,
  triggerRendererProps: PropTypes.any,
  hasValue: PropTypes.bool,
  isMultiple: PropTypes.bool,
  shouldHighlightIfHasValue: PropTypes.bool, // подсвечивает желтым если есть значение
  shouldShowResetButton: PropTypes.bool, // показывает кнопку сброса если есть значение
  // нужно в случае если делаем через модалку, сюда прокидывается состояние модалки открыта/закрыта
  // если открыта, то будет показывать стрелочку раскрытой, пока модалка не закроется
  shouldKeepTriggerOpened: PropTypes.bool,
  hasBorder: PropTypes.bool,
};

DropdownNextTrigger.displayName = 'DropdownNextTrigger';

DropdownNextTrigger.defaultProps = {
  label: '',
  className: '',
  value: {},
  placeholder: '',
  disabled: false,
  size: TRIGGER_SIZES[DEFAULT],
  closeOverlay: _noop,
  TriggerRenderer: DefaultTriggerRenderer,
  hasBorder: true,
};

export default DropdownNextTrigger;
